import { tokens } from "lib/colors"
import { useContext, useEffect, useMemo, useState } from "react"
import Text from "lib/components/Text"
import TitleWithLiveDot from "@components/shared-components/TitleWithLiveDot"
import { LanguageContext } from "context/LanguageContext"
import { isDesktop } from "react-device-detect"
import dynamic from "next/dynamic"
import jwtDecode from "jwt-decode"
import { useAuth, EmailCheckPayload } from "lib/auth"
import { captureException } from "@sentry/browser"
import { Token } from "@components/Layouts/LayoutWrapper"
import fetcher from "lib/fetcher"
import getSafeRedirectPath from "lib/safe-redirect"
import { gtmEvent } from "lib/gtm"
import { useRouter } from "next/router"
import { toast } from "react-toastify"
import { useCheckout } from "lib/checkout"
import Icon from "lib/components/Icons"
import { Controller, useForm } from "react-hook-form"
import Input from "lib/components/Input"
import InputPassword from "lib/components/InputPassword"
import Link from "next/link"
import Image from "next/image"
import Button from "lib/components/Button"
import { UserEmailCheck, UserEmailCheckMutationVariables } from "@graphql/auth/user_email_check"
import ModalChangeAccountPassword from "@components/Modal/ChangeAccountPassword"
import { requestNotificationPermission } from "lib/helpers"
import { getMessaging, getToken } from "firebase/messaging"
import app from "lib/firebase"
import InputPhone from "lib/components/InputPhone"
import { NextSeo } from "next-seo"
import useButtonCloseQiscus from "lib/hooks/useButtonCloseQiscus"
import url from "url"
import ButtonCloseQiscus from "@components/shared-components/ButtonCloseQiscus"
import QiscusOpen from "@components/shared-components/QiscusOpen"
import BulletRed from "@components/shared-components/BulletRed"
import safeLocalStorage from "lib/storage"
import useRateLimitCountdown, { formatRateLimitText, retryAfterSeconds } from "hooks/useRateLimitCountdown"

const GoogleLogin = dynamic(() => import("@react-oauth/google").then((mod) => mod.GoogleLogin), {
  ssr: false,
})

type ActiveStep = "register" | "login" | "login_phone" | "create_password"

export const registerServiceWorker = async () => {
  if ("serviceWorker" in navigator) {
    try {
      await navigator.serviceWorker.register("/firebase-messaging-sw.js")
    } catch (error) {
      /* eslint-disable no-console */
      console.error("Service worker registration failed:", error)
      /* eslint-disable no-console */
    }
  }
}

export async function askPermissionAndRegister(res) {
  try {
    const permission = await requestNotificationPermission()

    if (permission === "granted") {
      await registerServiceWorker()

      const messaging = getMessaging(app)
      const registration = await navigator.serviceWorker.getRegistration()

      const fcmToken = await getToken(messaging, {
        vapidKey: process.env.NEXT_PUBLIC_FIREBASE_FCM_VAPID_KEY,
        serviceWorkerRegistration: registration,
      })

      await fetcher("/api/fcm-token/update", "POST", {
        user_id: res.user_id,
        fcm_token: fcmToken,
      })
    }
  } catch (err) {
    captureException(err)
  }
}

export async function getStaticProps(ctx) {
  const activeLocale = ctx.locale

  const dataLanguage = await import(
    `../public/locales/${activeLocale.toUpperCase()}/translations.json`
  )

  const descriptionName = `${dataLanguage.login} ${dataLanguage.to_your_account}`
  const metaTitle = `${dataLanguage.login} | PasarTrainer`

  return {
    props: {
      metaTitle,
      descriptionName,
    },
  }
}

function LoginNew({ metaTitle, descriptionName }) {
  const router = useRouter()
  const { pathname } = url.parse(router.asPath)
  const { useTranslation: t } = useContext(LanguageContext)
  const { handleUserLogin, handleCheckEmail, handleUserProfile, isLoggedIn } = useAuth()
  const { checkoutData, removeCheckoutData } = useCheckout()
  const { closeQiscusApp, isQiscusHidden, openQiscusApp, newMessages } = useButtonCloseQiscus()

  const [isRealDesktop, setIsRealDesktop] = useState<boolean>(false)
  const [loadingButton, setLoadingButton] = useState<boolean>(false)
  const usernameRl = useRateLimitCountdown()
  const phoneRl = useRateLimitCountdown()
  const [googleWidth, setGoogleWidth] = useState<number>(252)
  const [openModalChangePassword, setOpenModalChangePassword] = useState<boolean>(false)
  const [activeStep, setActiveStep] = useState<ActiveStep>("login")

  const {
    r: redirectPath,
    activationToken: activationTokenParam,
    email: emailParam = "",
    classID,
    restrictedPage = true,
    order_id,
    type,
  }: any = router.query

  const {
    control,
    trigger,
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setFocus,
    setValue,
    setError,
  } = useForm<{
    username: string
    password: string
    confirm_password: string
    activation_token: string
    phone_number: string
    email: string
  }>()

  useEffect(() => {
    if (router.query) {
      if (router.query.step) {
        setActiveStep(router.query.step as ActiveStep)
      }
    }
  }, [router.query])

  useEffect(() => {
    if (isDesktop) {
      setIsRealDesktop(true)
    }
  }, [])

  useEffect(() => {
    const updateButtonWidth = () => {
      const viewportWidth = document.documentElement.clientWidth

      if (viewportWidth >= 1024) {
        setGoogleWidth(254) // Desktop
      } else if (viewportWidth >= 768) {
        setGoogleWidth(284.5) // Tablet
      } else if (viewportWidth >= 425) {
        setGoogleWidth(393) // Tablet
      } else if (viewportWidth >= 375) {
        setGoogleWidth(343) // Mobile medium
      } else {
        setGoogleWidth(288) // Mobile small
      }
    }

    updateButtonWidth()
    window.addEventListener("resize", updateButtonWidth)

    return () => window.removeEventListener("resize", updateButtonWidth)
  }, [])

  useMemo(() => {
    if (restrictedPage && isLoggedIn && !openModalChangePassword) {
      router.replace("/")
    }
  }, [restrictedPage, router, isLoggedIn, openModalChangePassword])

  useEffect(() => {
    if (activationTokenParam) {
      setValue("username", emailParam)
    }
  }, [activationTokenParam, emailParam, setValue])

  useEffect(() => {
    setFocus("username")
  }, [setFocus])


  const handleLogin = async (data) => {
    setLoadingButton(true)
    const emailOrUsername = data.username.toLowerCase() || emailParam
    try {
      const payloadCheckEmail: EmailCheckPayload = {
        email: emailOrUsername,
        is_google: false,
        purpose: "login",
      }
      if (classID) {
        payloadCheckEmail.class_id = classID
      }
      const resCheckEmail: UserEmailCheck = await handleCheckEmail(payloadCheckEmail)
      const loginType = resCheckEmail.login_type
      const hasPassword = resCheckEmail.credential_exist

      if (resCheckEmail.user_role !== "ptc-user" && resCheckEmail.user_role !== "ptc-company-admin") {
        throw new Error("ErrInvalidPlatform")
      }

      if (emailOrUsername.includes("@") && loginType === "email" && !hasPassword) {
        if (resCheckEmail.credential_available) {
          throw new Error("ErrInvalidLoginMethod")
        } else {
          router.push(`/forgot-password?email=${emailOrUsername}`)
        }
      }

      if (loginType !== "phone" && hasPassword) {
        const res = await handleUserLogin({
          password: data.password,
          username: data.username.toLowerCase() || emailParam,
        })
        if (res) {
          safeLocalStorage.setItem(
            "qismo-widget",
            JSON.stringify({
              unique_id: res.email,
              display_name: res.first_name,
              extra_fields: JSON.stringify([]),
              user_id: res.email,
              user_name: res.first_name,
            }),
          )
          fetcher("/api/auth/insert-login-history", "POST").catch(captureException)
          gtmEvent("ce_login", {
            user_id: res.user_id,
            custom_user_id: res.user_id,
            account_type: res.user_role !== "ptc-user" ? "Company" : "Individual",
            job_title: res.job_title,
            method: "Email",
          })
          setLoadingButton(false)
          document.cookie = "bubbleChat=closed; path=/; max-age=604800; Secure; SameSite=Strict"
          // use router reload to get fresh data from server after login
          if (order_id != null) {
            const dataPayload = {
              class_id: checkoutData.class_id,
              order_id,
              payment_method: checkoutData.payment_method,
              coupon_code: checkoutData.coupon_code,
            }
            await fetcher("/api/order/update", "POST", dataPayload)
            removeCheckoutData()
            toast.success(t("success_create_order"))
            router.replace(`/checkout/finish?type=${type}&orderId=${order_id}`)
          } else if (!res.is_login_before) {
            safeLocalStorage.setItem("is_login_before", JSON.stringify(res.is_login_before))
            setOpenModalChangePassword(true)
            await handleUserProfile()
          } else {
            safeLocalStorage.setItem("active_route", "home")
            if (redirectPath?.includes("my-classes")) {
              safeLocalStorage.setItem("active_route", "my-classes")
            }
            if (redirectPath?.includes("orders")) {
              safeLocalStorage.setItem("active_route", "orders")
            }
            if (redirectPath?.includes("inhouse-proposal")) {
              safeLocalStorage.setItem("active_route", "inhouse-proposal")
            }
            if (emailOrUsername.includes("@")) {
              toast.success(t("login_successful_with_email"))
            } else {
              toast.success(t("login_successful_with_username"))
            }
            setTimeout(() => {
              window.location.href = getSafeRedirectPath(redirectPath)
            }, 1000)
          }
          safeLocalStorage.setItem("sidebar", "show")
        }
      }
    } catch (err) {
      const statusCode = err?.response?.status
      const errorMessage = err?.response?.data?.message || err.message

      if (statusCode === 429) {
        usernameRl.handle429(err)
        return
      }

      if (errorMessage === "ErrInvalidPlatform") {
        toast.error(t("error_meesage_login_not_ptc_user"))
      }

      if (errorMessage === "ErrInvalidLoginMethod") {
        setError("username", { message: t("email_already_registered_please_log_in_with_google") })
      }

      if (errorMessage === "Oops, we cannot find your account") {
        if (emailOrUsername.includes("@")) {
          setError("username", { message: t("email_not_registered_please_use_a_different_one") })
        } else {
          setError("username", { message: t("username_not_registered_please_use_a_different_one") })
        }
      }
      if (errorMessage === "ErrInvalidPassword") {
        setError("password", {
          message: t("the_password_you_input_is_incorrect_please_try_again"),
        })
      }
    } finally {
      setLoadingButton(false)
    }
  }

  const handleLoginWithGoogle = async (
    params,
    redirectPathParams,
    classIDParams,
    orderID,
    typeParams,
  ) => {
    try {
      // Only email-check when a class context needs participant validation. The
      // platform (ptc-user/ptc-company-admin) gate now runs server-side in /api/login,
      // so Google no longer shares the email-check rate-limit counter with email login.
      if (classIDParams) {
        await handleCheckEmail({
          email: params.username,
          class_id: classIDParams,
          is_google: true,
          purpose: "login",
        })
      }
      const userLogin = await handleUserLogin(params)

      if (userLogin) {
        safeLocalStorage.setItem(
          "qismo-widget",
          JSON.stringify({
            unique_id: userLogin?.email,
            display_name: userLogin?.first_name,
            extra_fields: JSON.stringify([]),
            user_id: userLogin.email,
            user_name: userLogin.first_name,
          }),
        )
        fetcher("/api/auth/insert-login-history", "POST").catch(captureException)
        gtmEvent("ce_login", {
          user_id: userLogin.user_id,
          custom_user_id: userLogin.user_id,
          account_type: userLogin.user_role !== "ptc-user" ? "Company" : "Individual",
          job_title: userLogin.job_title,
          method: "Google",
        })
        document.cookie = "bubbleChat=closed; path=/; max-age=604800; Secure; SameSite=Strict"
        safeLocalStorage.setItem("sidebar", "show")
        if (orderID != null) {
          const getCheckout = safeLocalStorage.getItem("checkoutData")
          const dataCheckout = JSON.parse(getCheckout)
          const dataPayload = {
            class_id: dataCheckout.class_id,
            order_id: orderID,
            payment_method: dataCheckout.payment_method,
            coupon_code: dataCheckout.coupon_code,
          }
          await fetcher("/api/order/update", "POST", dataPayload)
          removeCheckoutData()
          toast.success("Success Create Order")
          router.replace(`/checkout/finish?type=${typeParams}&orderId=${orderID}`)
        } else {
          safeLocalStorage.setItem("active_route", "home")

          if (redirectPath?.includes("my-classes")) {
            safeLocalStorage.setItem("active_route", "my-classes")
          }

          if (redirectPath?.includes("orders")) {
            safeLocalStorage.setItem("active_route", "orders")
          }

          if (redirectPath?.includes("inhouse-proposal")) {
            safeLocalStorage.setItem("active_route", "inhouse-proposal")
          }
          toast.success(t("login_successful_with_google"))
          setTimeout(() => {
            window.location.replace(getSafeRedirectPath(redirectPathParams))
          }, 1000)
        }
      }
    } catch (error) {
      if (error?.response?.status === 429) {
        toast.error(formatRateLimitText(t("rate_limit_too_many_attempts"), retryAfterSeconds(error)))
        return
      }
      let message = error.response?.data?.message
      if (message === "ErrUserNotFound") {
        message = t("not_found_account")
      }
      if (message === "ErrInvalidPlatform") {
        message = t("error_meesage_login_not_ptc_user")
      }
      toast.error(message || "Oops: Account not found")
    }
  }

  const handleLoginPhone = async (data) => {
    setLoadingButton(true)
    try {
      const payloadCheckEmail: EmailCheckPayload = {
        email: data.phone_number,
        is_google: false,
        purpose: "login",
      }
      const res: UserEmailCheck = await handleCheckEmail(payloadCheckEmail)

      if (res.user_role !== "ptc-user" && res.user_role !== "ptc-company-admin") {
        throw new Error("ErrInvalidPlatform")
      }

      const loginType = res.login_type

      if (res.credential_exist && loginType === "phone") {
        safeLocalStorage.setItem("login_phone", data.phone_number)
        router.replace(
          redirectPath
            ? `/login-verification?r=${encodeURIComponent(getSafeRedirectPath(redirectPath))}`
            : "/login-verification",
        )
      }
    } catch (err) {
      const statusCode = err?.response?.status

      if (statusCode === 429) {
        phoneRl.handle429(err)
        return
      }

      if (err?.message === "ErrInvalidPlatform") {
        toast.error(t("error_meesage_login_not_ptc_user"))
        return
      }

      setError("phone_number", {
        message: t("the_phone_number_you_input_is_incorrect_please_try_again"),
      })
      captureException(err)
    } finally {
      setLoadingButton(false)
    }
  }

  return (
    <>
      <style>
        {`
          ${
            isQiscusHidden
              ? "#qismo-widget {display:none} #qiscus-app-button-close { display:none }"
              : ""
          }
        `}
      </style>
      <NextSeo
        title={metaTitle}
        description={descriptionName}
        canonical={`${process.env.NEXT_PUBLIC_URL}${pathname}`}
        noindex
      />
      <main className="relative min-h-screen flex flex-col items-center bg-white pb-24 lg:pb-0">
        {/* === Accent Elements (desktop only) === */}
        <div className="hidden lg:block absolute top-0 left-0 z-0">
          <Image
            src="/assets/svg/auth/dot-accent.svg"
            alt="Accent Dot Top Left"
            width={120}
            height={120}
            className="pointer-events-none select-none"
          />
        </div>

        <div className="hidden lg:block absolute top-0 right-0 z-0">
          <Image
            src="/assets/svg/auth/blur-top-right.svg"
            alt="Blur Top Right"
            width={300}
            height={300}
            className="pointer-events-none select-none"
          />
        </div>

        <div className="hidden lg:block absolute bottom-0 left-0 z-0">
          <Image
            src="/assets/svg/auth/blur-bottom-left.svg"
            alt="Blur Bottom Left"
            width={300}
            height={300}
            className="pointer-events-none select-none"
          />
        </div>
        {/* Header */}
        <header className="w-full h-fit pt-4 lg:pt-8">
          <Link href="/">
            <div className="max-w-7xl mx-auto flex px-4 lg:px-8">
              <Image
                src="/assets/images/logo-large-primary.png"
                alt="Logo"
                width={isRealDesktop ? 222 : 144}
                height={isRealDesktop ? 48 : 31}
                className="pointer-events-none select-none"
              />
            </div>
          </Link>
        </header>

        {/* Main content */}
        <div className="flex-1 w-full relative z-10">
          <div className="max-w-7xl mx-auto px-4 lg:px-8">
            <div className="flex flex-col lg:flex-row min-h-[calc(100vh-5rem)]">
              {/* Left Side */}
              <div className="lg:flex-1 flex items-center justify-start pt-6 lg:py-16">
                <div className="relative">
                  <TitleWithLiveDot alignText="left" />
                  <Text
                    customClass="text-xl sm:text-[48px] text-[#FFBE0B] leading-tight"
                    weight="semibold"
                    color="light"
                    alignText="left">
                    <span className="text-primary hidden sm:inline">{t("in_indonesia")}</span>{" "}
                    <span className="text-primary initial sm:hidden">{t("in_indonesia")}</span>
                  </Text>
                </div>
              </div>

              {/* Right Side */}
              <div className="lg:flex-1 flex items-center justify-center pt-8 lg:py-16">
                {/* Form Login */}
                {activeStep === "login" && (
                  <div className="w-full max-w-[569px] bg-white lg:py-10 lg:px-6 lg:rounded-[20px] lg:drop-shadow-[0px_4px_35px_rgba(0,0,0,0.08)]">
                    <h2 className="text-xl lg:text-[32px] font-medium mb-3.5 lg:mb-12 text-neutral-70">
                      {t("login")}
                    </h2>

                    <form onSubmit={handleSubmit(handleLogin)} autoComplete="off">
                      <div className="space-y-6">
                        <Input
                          type="text"
                          label={`${t("email_address_or_username")}`}
                          name="username"
                          {...register("username", {
                            required: t("email_username_required"),
                          })}
                          error={!!errors.username}
                          message={errors.username && errors.username.message}
                          placeholder={t("input_username_or_email")}
                          defaultValue={emailParam}
                          autoComplete="off"
                        />

                        <div>
                          <InputPassword
                            label={`${t("password")}`}
                            placeholder={t("input_password")}
                            {...register("password", {
                              required: t("password_required"),
                            })}
                            error={!!errors.password || usernameRl.isBlocked}
                            message={
                              usernameRl.isBlocked
                                ? formatRateLimitText(t("incorrect_password_error"), usernameRl.remaining)
                                : errors.password && (errors.password.message as string)
                            }
                          />
                          <div className="text-right mt-1">
                            <Link
                              href={
                                watch("username")
                                  ? `/forgot-password?email=${watch("username")}`
                                  : "/forgot-password"
                              }
                              className="text-xs lg:text-sm text-primary font-medium hover:underline">
                              {t("forgot_password?")}
                            </Link>
                          </div>
                        </div>

                        <Button
                          variant="contained"
                          color={
                            !watch("username") || !watch("password") || usernameRl.isBlocked
                              ? "gray"
                              : "primary"
                          }
                          fullWidth
                          type="submit"
                          loading={loadingButton}
                          disabled={
                            !watch("username") ||
                            !watch("password") ||
                            loadingButton ||
                            usernameRl.isBlocked
                          }
                          customClass="h-[48px] !opacity-100">
                          {t("login")}
                        </Button>
                      </div>
                    </form>

                    {/* Separator */}
                    <div className="mt-10 mb-6 flex items-center">
                      <div className="flex-1 h-px bg-neutral-40" />
                      <span className="px-3 text-base text-neutral-50">{t("or_login_with")}</span>
                      <div className="flex-1 h-px bg-neutral-40" />
                    </div>

                    {/* Social buttons */}
                    <div className="flex flex-col md:flex-row justify-between gap-3">
                      <div className="w-full md:w-1/2 flex items-center justify-center">
                        <GoogleLogin
                          onSuccess={async (credentialResponse) => {
                            try {
                              // Default home; the `?r=` param below overrides. Using the current
                              // pathname here sent a no-`?r=` login back to /login, bouncing through
                              // the isLoggedIn guard before landing on "/".
                              let redirectPathValue = "/"
                              const { email } = jwtDecode<Token>(credentialResponse.credential)
                              const url_string = window.location.href
                              const urlValuePath = new URL(url_string)
                              const classIDValue = urlValuePath.searchParams.get("classID")
                              const orderIDValue = urlValuePath.searchParams.get("order_id")
                              const typeProgram = urlValuePath.searchParams.get("type")

                              if (urlValuePath.searchParams.get("r")) {
                                redirectPathValue = urlValuePath.searchParams.get("r")
                              }

                              const params = {
                                username: email,
                                password: credentialResponse.credential,
                                loginType: "google",
                              }
                              await handleLoginWithGoogle(
                                params,
                                redirectPathValue,
                                classIDValue,
                                orderIDValue,
                                typeProgram,
                              )
                            } catch (error) {
                              captureException(error.message)
                            }
                          }}
                          onError={() => {
                            toast.error(t("error_message"))
                          }}
                          width={googleWidth}
                        />
                      </div>
                      <button
                        type="button"
                        className="flex items-center justify-center w-full md:w-1/2 border border-neutral-40 rounded py-2 text-sm font-medium"
                        onClick={() => setActiveStep("login_phone")}>
                        <Icon
                          iconName="device-mobile"
                          color="#111827"
                          type="stroke"
                          styles="w-5 h-5 mr-2"
                        />
                        {t("phone_number")}
                      </button>
                    </div>

                    <Text alignText="center" customClass="text-sm !text-neutral-50 mt-10">
                      {t("dont_have_an_account?")}{" "}
                      <button
                        type="button"
                        className="text-primary hover:underline"
                        onClick={() => router.push("/register")}>
                        {t("sign_up")}
                      </button>
                    </Text>
                  </div>
                )}

                {/* Form Login Phone */}
                {activeStep === "login_phone" && (
                  <div className="w-full max-w-[569px] bg-white lg:p-10 lg:rounded-[20px] lg:drop-shadow-[0px_4px_35px_rgba(0,0,0,0.08)]">
                    <Icon
                      iconName="arrow-left"
                      color={tokens.neutral[70]}
                      styles="cursor-pointer mb-3"
                      onClick={() => setActiveStep("login")}
                    />

                    <div className="flex flex-col mb-8">
                      <Text customClass="text-base" weight="semibold">
                        {t("input_phone_number")}
                      </Text>
                      <Text customClass="text-base">
                        {t("we_will_verify_your_phone_number_by_sending_an_otp")}
                      </Text>
                    </div>

                    <form onSubmit={handleSubmit(handleLoginPhone)} autoComplete="off">
                      <Controller
                        control={control}
                        name="phone_number"
                        rules={{
                          required: t("phone_number_required"),
                          minLength: {
                            value: 8,
                            message: t("phone_number_must_match_rule"),
                          },
                          maxLength: {
                            value: 14,
                            message: t("phone_number_must_match_rule"),
                          },
                        }}
                        render={({ field: { onChange, value } }) => (
                          <InputPhone
                            defaultCountry="id"
                            countryCodeEditable
                            enableSearch
                            disableSearchIcon
                            label={`${t("phone_number")}`}
                            placeholder={t("enter_phone")}
                            error={!!errors?.phone_number || phoneRl.isBlocked}
                            message={
                              phoneRl.isBlocked
                                ? formatRateLimitText(
                                    t("rate_limit_too_many_attempts"),
                                    phoneRl.remaining,
                                  )
                                : errors?.phone_number?.message?.toString()
                            }
                            value={value}
                            onChange={(event) => {
                              onChange(event)
                              trigger("phone_number")
                            }}
                          />
                        )}
                      />
                      <Button
                        variant="contained"
                        color={
                          !watch("phone_number") || phoneRl.isBlocked ? "gray" : "primary"
                        }
                        fullWidth
                        type="submit"
                        loading={loadingButton}
                        disabled={!watch("phone_number") || loadingButton || phoneRl.isBlocked}
                        customClass="h-[48px] !opacity-100 mt-6">
                        {t("send_otp")}
                      </Button>
                    </form>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      {openModalChangePassword && <ModalChangeAccountPassword isOpen={openModalChangePassword} />}
      <ButtonCloseQiscus closeQiscusApp={closeQiscusApp} />
      <BulletRed withNumberCount count={newMessages.length} isShow={!isQiscusHidden} />
      {isQiscusHidden && <QiscusOpen openQiscusApp={openQiscusApp} />}
    </>
  )
}

LoginNew.layout = "no"

export default LoginNew
